<script lang="ts" setup>
import { ref } from 'vue';
import { $fetch } from '@/plugins/fetch';
import { useI18n } from 'vue-i18n';
import InfiniteMarquee from '@/components/InfiniteMarquee.vue';
import useMarkdown from '@/hooks/markdown';
import { useAsyncData } from '@/hooks/async-data';

const { locale } = useI18n();
const marked = useMarkdown();

const isZh = locale.value === 'zh-CN';
const MAX_LIMIT = 5;

const lookMore = ref(true);

const { data: summaryList } = useAsyncData(
  'law:salon:summary',
  () => $fetch('/law/resource/v1/list', {
    data: {
      category: import.meta.env.VITE_LAW_SALON_CATEGORY_ID1,
      limit: -1,
    },
  }),
);

const { data: contactUsList } = useAsyncData(
  'law:salon:contactUs',
  () => $fetch('/law/resource/v1/list', {
    data: {
      category: import.meta.env.VITE_LAW_SALON_CATEGORY_ID2,
      limit: -1,
    },
  }),
);

const { data: listing } = useAsyncData(
  'law:salon:listing',
  () => $fetch('/news/v1/list', { data: { cid: 'hHUj4V', limit: -1 } }),
  {
    transform: (data) => data?.newsVOList ?? [],
  },
);

const { data: images } = useAsyncData(
  'law:salon:images',
  () => $fetch('/law/resource/v1/list', { data: { category: 'iZwGnM', limit: -1 } }),
);
</script>

<template>
  <div class="page-law-salon color-hex-2d2d2d">
    <div>
      <template v-if="summaryList?.length">
        <div class="header w-full p-32px <md:p-24" v-for="item in summaryList" :key="item.title">
          <h2 class="m-0 text-32px color-hex-1180ff font-medium leading-48px <md:text-26">{{ item.title }}</h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="paragraph m-0 mt-16px" v-html="marked.parse(item.content)"></div>
        </div>
      </template>

      <template v-if="contactUsList?.length">
        <template v-for="item in contactUsList" :key="item.title">
          <h2 class="mb-32px mt-48px text-center text-32px font-500 leading-48px <md:text-26">{{ item.title }}</h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="paragraph" v-html="marked.parse(item.content)"></div>
        </template>
      </template>
    </div>

    <div class="figure-card mt-48px py-48px" v-if="images?.length">
      <div class="container">
        <InfiniteMarquee :speed="30">
          <div class="relative mx-12px h-240px w-400px" v-for="(item, index) in images" :key="index" :title="item.title">
            <img :data-src="item.cover" class="lazyload block h-full w-full rounded-4px object-cover" :alt="item.title">
            <h3 class="absolute bottom-0 left-0 m-0 h-40px w-full truncate rounded-b-4px bg-[rgba(17,128,255,0.20)] px-12px text-center text-16px text-white font-normal leading-40px backdrop-blur-9px">
              {{ item.title }}
            </h3>
          </div>
        </InfiniteMarquee>
      </div>
    </div>

    <div v-if="isZh">
      <h2 class="m-0 pt-48px text-center text-32px font-500 leading-48px <md:text-26">往期回顾</h2>
      <ul class="m-0 list-none p-0">
        <li class="mt-32px" v-for="item in lookMore ? listing?.slice(0, MAX_LIMIT) : listing" :key="item.id">
          <RouterLink :to="`/journalism/article/${item.id}`" class="group block flex no-underline <md:flex-col">
            <div class="h-152px w-272px flex-shrink-0 <md:h-200 <md:w-full">
              <img class="lazyload block h-full w-full border-hex-979797 rounded-4px object-cover" :data-src="item.cover" :alt="item.title">
            </div>
            <div class="ml-24px flex flex-grow basis-0 flex-col justify-between <md:ml-0 <md:mt-16">
              <h3 class="truncate-ellipsis m-0 truncate text-24px color-hex-2d2d2d font-medium leading-36px <md:text-18 <md:leading-28 group-hover:color-hex-1180FF">
                <span>{{ item.title }}</span>
              </h3>
              <p class="line-clamp-2 m-0 mt-24px flex-grow text-16px color-hex-9A9EA6 leading-24px <md:mt-12 <md:text-14">{{ item.intro }}</p>
              <small class="mt-24px block text-right text-14px color-hex-ADB1BA leading-28px <md:mt-12">{{ item.releaseTime }}</small>
            </div>
          </RouterLink>
        </li>
      </ul>

      <div class="mx-auto mt-32px h-22px flex items-center justify-center text-16px color-hex-1180ff font-400" v-show="(listing?.length > MAX_LIMIT) ? lookMore : false">
        <span class="flex cursor-pointer items-center" @click="lookMore = false">
          <span>查看更多</span>
          <span class="i-home-icons:arrow-down"></span>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.paragraph {
  &:deep() {
    p {
      @apply my-0 not-first:mt-24px text-16px font-normal leading-24px;
    }

    a {
      color: #1180ff;
      text-decoration: none;
      list-style: none;
    }
  }
}

.figure-card {
  background: rgba(45, 45, 45, 0.02);
}

.header {
  box-sizing: border-box;
  background: url(https://res.oafimg.cn/-/d9a927469ffea0c9/3.png) no-repeat;
  background-color: #fcfcfc;
  background-position: 862px 16px;
  background-size: 482px 159px;
}

@screen lt-2xl {
  .header {
    box-sizing: border-box;
    background: url(https://res.oafimg.cn/-/d9a927469ffea0c9/3.png) no-repeat;
    background-color: #fcfcfc;
    background-position: 702px 16px;
    background-size: 482px 159px;
  }
}
</style>
